import { EnvelopeSimple, FileMagnifyingGlass, ArrowSquareOut, Clock } from '@phosphor-icons/react';
import { resolveStageId } from '../lib/stageModel';

function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
}

// Whole days from today until the given date. Null if unparseable.
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const today = new Date(new Date().toDateString());
  return Math.ceil((d - today) / 86400000);
}

// Inline Office Action guidance shown under the timeline on a My Matters card for matters
// with an outstanding USPTO Office Action. Mirrors the Onboarding / Search Completed layout.
// Deliberately does NOT get into substantive vs. non-substantive or push a call — it simply
// reassures, points the client to the email we sent, and links to the USPTO record.
export function OfficeActionInline({ matter }) {
  const tsdrUrl = matter?.tsdr_links?.status_url;
  const oaDeadline = (matter?.uspto_deadlines || []).find(
    (d) => (d.label || '').toLowerCase().includes('office action')
  );
  // Prefer the HubSpot-attached due date, fall back to the TSDR-computed one.
  const rawDue = matter?.office_action_due_date || oaDeadline?.actionable_date;
  const dueDate = formatDate(rawDue);
  const submitted = !!matter?.office_action_submitted;
  const daysLeft = daysUntil(rawDue);
  // Gentle countdown only when the deadline is real, in the future, and unanswered.
  const showCountdown = !submitted && rawDue && daysLeft !== null && daysLeft >= 0;
  // Stage-aware: 3779796689 = firm has reviewed & needs the client's go-ahead;
  // 3779796688 (and default) = firm is still reviewing — nothing needed from the client yet.
  const needsYou = resolveStageId(matter) === '3779796689';

  return (
    <div className="mt-4 pt-4" style={{ borderTop: '1px solid #ece5d8' }} data-testid={`office-action-inline-${matter.id}`}>
      {/* Header + stage badge */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h4 className="text-sm font-bold uppercase tracking-wide" style={{ color: '#1d57a5' }}>The USPTO issued an Office Action</h4>
        {needsYou ? (
          <span className="text-xs font-bold px-2 py-1" style={{ backgroundColor: '#c2410c', color: '#ffffff' }}>Action needed</span>
        ) : (
          <span className="text-xs font-bold px-2 py-1" style={{ backgroundColor: '#141b4d', color: '#ffffff' }}>We're reviewing it</span>
        )}
      </div>

      {/* What's happening / what's needed — set by stage */}
      <p className="text-sm mb-4 leading-relaxed" style={{ color: '#101820' }}>
        {needsYou
          ? "We've reviewed the Office Action and emailed you our recommendation. Reply to that email and we'll prepare and file your response — that's the only thing we need from you."
          : "We've received the Office Action and we're reviewing what it means for your mark. We'll email you a plain-English recommendation shortly — nothing is needed from you right now."}
      </p>

      {/* Gentle response countdown */}
      {showCountdown && (
        <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full" style={{ backgroundColor: needsYou ? '#fdf2e6' : '#eef2f9', border: `1px solid ${needsYou ? '#f0d3b0' : '#c9d6ea'}` }}
          data-testid={`oa-countdown-${matter.id}`}>
          <Clock weight="fill" className="w-4 h-4" style={{ color: needsYou ? '#c2410c' : '#1d57a5' }} />
          <span className="text-sm font-semibold" style={{ color: needsYou ? '#7c2d12' : '#141b4d' }}>
            {daysLeft === 0 ? 'Response due today' : `Response due in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`}
          </span>
          {dueDate && <span className="text-xs" style={{ color: needsYou ? '#9a5a2a' : '#5b6b86' }}>· {dueDate}{needsYou ? '' : ' · we\u2019re on it'}</span>}
        </div>
      )}

      {/* Intro line */}
      <p className="text-xs mb-4 leading-relaxed" style={{ color: '#9a9488' }}>
        An Office Action is a standard letter from the USPTO examiner asking for more information before your
        application can move forward. It's a normal part of the process — not a rejection{dueDate ? `, and there's a response deadline of ${dueDate}` : ''}.
      </p>

      {/* Two tiles */}
      <div className="grid sm:grid-cols-2 gap-3">
        {/* We emailed you */}
        <div className="p-4 h-full" style={{ backgroundColor: '#ffffff', border: '1px solid #ece5d8', borderTop: '3px solid #ffd100' }}
          data-testid={`oa-email-tile-${matter.id}`}>
          <div className="flex items-center gap-1.5 mb-2">
            <EnvelopeSimple weight="fill" className="w-4 h-4" style={{ color: '#c99a00' }} />
            <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#141b4d' }}>Check your email</span>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>
            We've sent you an email explaining what this Office Action means and the next steps. Search your inbox for Indie Law.
          </p>
        </div>

        {/* View the OA on TSDR */}
        <div className="p-4 h-full flex flex-col" style={{ backgroundColor: '#ffffff', border: '1px solid #ece5d8', borderTop: '3px solid #141b4d' }}
          data-testid={`oa-tsdr-tile-${matter.id}`}>
          <div className="flex items-center gap-1.5 mb-2">
            <FileMagnifyingGlass weight="fill" className="w-4 h-4" style={{ color: '#1d57a5' }} />
            <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#141b4d' }}>The official record</span>
          </div>
          <p className="text-sm leading-relaxed mb-4" style={{ color: '#52525B' }}>
            You can read the Office Action the USPTO filed on the public TSDR record. Once it opens, click the{' '}
            <span className="font-semibold" style={{ color: '#141b4d' }}>"Documents"</span> tab to find and open the Office Action.
          </p>
          {tsdrUrl && (
            <a href={tsdrUrl} target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="mt-auto inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-3 transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: '#141b4d', color: '#ffffff' }}
              data-testid={`oa-tsdr-link-${matter.id}`}>
              <FileMagnifyingGlass weight="fill" className="w-4 h-4" /> View on USPTO (TSDR)
              <ArrowSquareOut weight="bold" className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
